import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Inventario = () => {
  const { user } = useAuth();
  const [inventario, setInventario] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  
  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;

  useEffect(() => {
    fetchInventario();
    if (isMainAdmin) {
      fetchEmpresas();
    }
  }, [isMainAdmin]);
  
  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const fetchInventario = async () => {
    setLoading(true);
    try {
      const response = await api.get('/inventario');
      setInventario(response.data);
    } catch (error) {
      message.error('Error al cargar inventario');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      if (editingItem) {
        await api.put(`/inventario/${editingItem.id}`, values);
        message.success('Item actualizado exitosamente');
      } else {
        await api.post('/inventario', values);
        message.success('Item creado exitosamente');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      fetchInventario();
    } catch (error) {
      message.error('Error al guardar item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      ...record,
      empresa_id: record.empresa_id || undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/inventario/${id}`);
      message.success('Item eliminado exitosamente');
      fetchInventario();
    } catch (error) {
      message.error('Error al eliminar item');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria',
      key: 'categoria',
    },
    {
      title: 'Precio',
      dataIndex: 'precio',
      key: 'precio',
      render: (precio) => `S/ ${precio}`,
    },
    {
      title: 'Stock',
      dataIndex: 'stock',
      key: 'stock',
      render: (stock, record) => (
        <Tag color={stock <= record.stock_minimo ? 'red' : 'green'}>
          {stock}
        </Tag>
      ),
    },
    {
      title: 'Stock Mínimo',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa_nombre',
      render: (text) => text || '-',
    }] : []),
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="¿Estás seguro de eliminar este item?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Inventario"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingItem(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            Nuevo Item
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={inventario}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title={editingItem ? 'Editar Item' : 'Nuevo Item'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingItem(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="codigo"
            label="Código"
            rules={[{ required: true, message: 'Ingresa el código' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="categoria" label="Categoría">
            <Select placeholder="Selecciona una categoría">
              <Select.Option value="Repuestos">Repuestos</Select.Option>
              <Select.Option value="Lubricantes">Lubricantes</Select.Option>
              <Select.Option value="Herramientas">Herramientas</Select.Option>
              <Select.Option value="Otros">Otros</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="precio"
            label="Precio"
            rules={[{ required: true, message: 'Ingresa el precio' }]}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="stock"
            label="Stock"
            rules={[{ required: true, message: 'Ingresa el stock' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="stock_minimo"
            label="Stock Mínimo"
            rules={[{ required: true, message: 'Ingresa el stock mínimo' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {isMainAdmin && (
            <Form.Item name="empresa_id" label="Empresa">
              <Select placeholder="Selecciona una empresa" allowClear>
                {empresas.map((empresa) => (
                  <Select.Option key={empresa.id} value={empresa.id}>
                    {empresa.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingItem ? 'Actualizar' : 'Crear'}
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingItem(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Inventario;

